/**
 * Client-side transcript normalizer.
 *
 * Mirrors backend/services/transcript_normalizer.py using the same
 * project glossary shape. We do it client-side so live transcript
 * updates render normalized text without a network round-trip per turn.
 *
 * Contract:
 *   normalize(raw, glossary) →
 *     { raw, normalized, corrections: [{start,end,raw,canonical,confidence,reason}] }
 *
 * If glossary is null/empty or raw is empty, returns identity — this
 * function must never rewrite text it wasn't told to rewrite.
 */

function escapeRegex(s) {
    return s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

function entryPattern(entry) {
    if (!entry?.variants?.length) return null;
    // Sort variants by descending length so longer matches win (e.g.
    // "founder counsel" beats "counsel"). Escape then join with |, wrap in
    // word boundaries, case-insensitive.
    const alts = [...entry.variants]
        .sort((a, b) => b.length - a.length)
        .map(escapeRegex)
        .join("|");
    return new RegExp(`\\b(${alts})\\b`, "gi");
}

export function normalizeTranscript(raw, glossary) {
    if (!raw || !glossary?.entries?.length) {
        return { raw: raw ?? "", normalized: raw ?? "", corrections: [] };
    }
    const hits = [];
    const claimed = [];

    for (const entry of glossary.entries) {
        const pat = entryPattern(entry);
        if (!pat) continue;
        let m;
        while ((m = pat.exec(raw)) !== null) {
            const start = m.index;
            const end = start + m[0].length;
            if (claimed.some(([a, b]) => a < end && start < b)) continue;
            hits.push({
                start,
                end,
                raw: m[0],
                canonical: entry.canonical,
                confidence: entry.confidence ?? "high",
                reason: "variant_match",
            });
            claimed.push([start, end]);
        }
    }

    if (!hits.length) return { raw, normalized: raw, corrections: [] };

    hits.sort((a, b) => a.start - b.start);
    let normalized = raw;
    for (let i = hits.length - 1; i >= 0; i--) {
        const c = hits[i];
        normalized =
            normalized.slice(0, c.start) +
            c.canonical +
            normalized.slice(c.end);
    }
    return { raw, normalized, corrections: hits };
}
